import _ from 'lodash';
import moment from 'moment';
import type { StateReconciler } from 'redux-persist';
import type { ReduxState } from './state';

type ProcessStateFunc =
  (inboundState: ReduxState, originalState: ReduxState, reconciledState: ReduxState) => ReduxState;

const TEN_MINUTES = 600_000;

const processTicketCache: ProcessStateFunc = (inboundState, originalState, reconciledState) => {
  if (!inboundState.ticket?.cache) return reconciledState;

  const now = Date.now();
  const { cache } = inboundState.ticket;

  const data: typeof inboundState['ticket']['data'] = {};
  const event = _.cloneDeep(originalState.event);
  const contact = _.cloneDeep(originalState.contact);

  // Ensure cache buckets exist
  cache.created = cache.created || {};
  cache.updated = cache.updated || {};

  const processTicketsByCacheType = (
    ticketGroup: typeof cache.created | typeof cache.updated,
    getTimestamp: (ticket: any) => number | undefined,
  ) => {
    Object.entries(ticketGroup).forEach(([subdomain, tickets]) => {
      Object.entries(tickets).forEach(([uuid, ticket]) => {
        const timestamp = getTimestamp(ticket);
        const isStale = !timestamp || now - timestamp > TEN_MINUTES;

        if (isStale) {
          delete tickets[uuid]; // eslint-disable-line no-param-reassign
          return;
        }

        const ticketData = inboundState.ticket.data?.[uuid];
        if (!ticketData) return;

        data[uuid] = ticketData;

        const { contactId, lastActivityId } = ticketData;

        if (contactId) {
          const contactData = inboundState.contact?.[contactId];
          if (contactData) {
            contact[contactId] = contactData;
          }
        }

        if (lastActivityId) {
          const activityData = inboundState.event.data?.[uuid]?.data?.[lastActivityId];
          if (activityData) {
            _.set(event, ['data', uuid, 'data', lastActivityId], activityData);
          }
        }
      });
    });
  };

  processTicketsByCacheType(cache.created, ticket => ticket.createdAt);
  processTicketsByCacheType(cache.updated, ticket => ticket.updatedAt);

  return {
    ...reconciledState,
    ticket: {
      ...originalState.ticket,
      cache,
      data,
    },
    contact,
    event,
  };
};

const processLocalEvents: ProcessStateFunc = (inboundState, originalState, reconciledState) => {
  if (!inboundState.event.local) return reconciledState;

  const event = { ...reconciledState.event, local: {} };
  const ticket = { ...reconciledState.ticket, local: {} };

  Object.keys(inboundState.event.local).forEach((ticketId) => {
    const data = inboundState.event.local[ticketId];
    const timestamps = Object.keys(data);
    const toKeep = timestamps.filter((timestamp) => Number(timestamp) > Number(moment().subtract(1, 'week')));
    toKeep.forEach((timestamp) => {
      const eventData = data[timestamp];
      event.local[ticketId] ??= {};
      ticket.local[ticketId] ??= {};
      _.set(event, ['local', ticketId, timestamp], eventData);
      _.set(event, ['data', ticketId, 'data', eventData.id], eventData);
      _.set(ticket, ['local', ticketId, timestamp], eventData.id);
    });
  });

  return Object.assign(reconciledState, {
    event,
    ticket,
  });
};

export const stateReconciler: StateReconciler<ReduxState> = (inboundState, originalState) => {
  if (!inboundState) return originalState;

  let reconciledState = { ...originalState };
  reconciledState = processTicketCache(inboundState, originalState, reconciledState);
  reconciledState = processLocalEvents(inboundState, originalState, reconciledState);
  reconciledState.analytics = inboundState.analytics;

  return reconciledState;
};
